import type { BoxProps } from '@mui/material/Box';
import type { Breakpoint } from '@mui/material/styles';
import type { MotionProps, MotionValue, SpringOptions } from 'framer-motion';

import { Button, Card, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { m, useScroll, useSpring, useTransform, useMotionValueEvent } from 'framer-motion';
import { useRef, useState } from 'react';
import { varFade, MotionContainer } from 'src/components/animate';

import { HeroBackground } from './hero-background';
import { HeroHomeSection } from '@/types/sections/HeroHomeSection';
import { getCtaUrl } from '@/types/Cta';
import Icon, { getIconName } from '@/components/Icon/Icon';

// ----------------------------------------------------------------------

const mdKey: Breakpoint = 'md';
const lgKey: Breakpoint = 'lg';

type HeroHomeProps = BoxProps & {
  section: HeroHomeSection;
};

const motionProps: MotionProps = {
  variants: varFade('inUp', { distance: 24 }),
};

export default function HeroHome({ sx, section, ...other }: HeroHomeProps) {
  const scrollProgress = useScrollPercent();

  const mdUp = useMediaQuery((theme) => theme.breakpoints.up(mdKey));

  const distance = mdUp ? scrollProgress.percent : 0;

  const y1 = useTransformY(scrollProgress.scrollY, distance * -7);

  const opacity: MotionValue<number> = useTransform(
    scrollProgress.scrollY,
    [0, 1],
    [1, mdUp ? Number((1 - scrollProgress.percent / 80).toFixed(1)) : 1]
  );

  const renderHeading = () => (
    <m.div {...motionProps}>
      <Box
        component="h1"
        color="common.white"
        sx={[
          (theme) => ({
            my: 0,
            py: 0,
            flexWrap: 'wrap',
            typography: 'h1',
            [theme.breakpoints.down(mdKey)]: {
              fontSize: 58,
              lineHeight: 80 / 70,
              fontWeight: theme.typography.fontWeightSemiBold,
            },
            [theme.breakpoints.up(mdKey)]: {
              fontSize: 64,
              lineHeight: 80 / 70,
              fontWeight: theme.typography.fontWeightSemiBold,
            },
            [theme.breakpoints.up(lgKey)]: {
              fontSize: 68,
              lineHeight: 80 / 70,
              fontWeight: theme.typography.fontWeightSemiBold,
            },
          }),
        ]}>
        RoboOne
      </Box>
      <Box
        component="h3"
        color="common.white"
        sx={[
          (theme) => ({
            typography: 'h3',
            [theme.breakpoints.down(mdKey)]: {
              fontSize: 24,
              lineHeight: '20px',
              fontWeight: theme.typography.fontWeightSemiBold,
            },
            [theme.breakpoints.up(mdKey)]: {
              fontSize: 26,
              lineHeight: '20px',
              fontWeight: theme.typography.fontWeightSemiBold,
            },
            [theme.breakpoints.up(lgKey)]: {
              fontSize: 28,
              lineHeight: '20px',
              fontWeight: theme.typography.fontWeightSemiBold,
            },
            my: 0,
            py: 0,
          }),
        ]}>
        monitoring made easy
      </Box>
    </m.div>
  );

  const renderCard = () => (
    <Box component={m.div} variants={varFade('in')} sx={{ height: '100%', minWidth: '50%' }}>
      <Card sx={{ borderRadius: 0, p: 4 }}>
        { section.word && (
          <Typography variant="overline" sx={{ color: 'text.disabled' }}>
            {section.word}
          </Typography>
        )}
        
        { section.title && (
          <Typography variant="h2" sx={{ color: 'primary.main' }}>
            {section.title}
          </Typography>
        )}

        { section.subtitle && (
           <Typography sx={{ color: 'text.secondary', mt: 2 }}>
            {section.subtitle}
          </Typography>
        )}

        {section.cta2  && (
          <Button
            size="large"
            color="primary"
            variant="contained"
            href={getCtaUrl(section.cta2)}
            sx={{ mt: 4, mr: 2, borderRadius: 0, bgcolor: 'common.white', color: 'primary.main', border: 1, borderColor: 'primary.lighter' }}
            startIcon={ section.cta2.icon && (
              <Icon name={getIconName(section.cta2.icon, 'unknown')} color={'primary.lighter'} />
              )}
            >
            {section.cta2.title}
          </Button>
        )}

        {section.cta1 && (
          <Button
            size="large"
            color="primary"
            variant="contained"
            href={getCtaUrl(section.cta1)}
            sx={{ mt: 4, mr: 2, borderRadius: 0 }}
            startIcon={ section.cta1.icon && (
              <Icon name={getIconName(section.cta1.icon, 'unknown')} color={'common.white'} />
              )}
            >
            {section.cta1.title}
          </Button>
        )}
      </Card>
    </Box>
  );

  const renderWheel = () => (
    <Box
      sx={{
        alignItems: 'center',
        position: 'relative',
        width: '125%', // Make the image container bigger than the parent
        height: '125%', // Make the image container bigger than the parent
        //transform: 'translateX(10%)', // Shift right by 25%
      }}
    >
      <m.div
        style={{ y: y1 }}
        className="box"
        transition={{ ease: 'linear', duration: 10, repeat: Infinity }}
        animate={{ rotate: 360 }}>
        <img src="/assets/images/home/graph.svg" width="100%" height="100%" />
      </m.div>
    </Box>
  );

  return (
    <Box
      ref={scrollProgress.elementRef}
      component="section"
      sx={[
        (theme) => ({
          overflow: 'hidden',
          position: 'relative',
          [theme.breakpoints.up(mdKey)]: {
            minHeight: 760,
            height: '100vh',
            maxHeight: 1440,
            display: 'block',
            willChange: 'opacity',
            mt: 'calc(var(--layout-header-desktop-height) * -1)',
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}>
      <Box
        component={m.div}
        style={{ opacity }}
        sx={[
          (theme) => ({
            width: 1,
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
            transition: theme.transitions.create(['opacity']),
            [theme.breakpoints.up(mdKey)]: {
              height: 1,
              position: 'fixed',
              maxHeight: 'inherit',
            },
          }),
        ]}>
        <HeroBackground media={section.media}/>
        <Container
          component={MotionContainer}
          sx={[
            (theme) => ({
              py: 'calc(var(--layout-header-desktop-height))',
              zIndex: 9,
              [theme.breakpoints.up(mdKey)]: {
                flex: '1 1 auto',
                justifyContent: 'center',
              },
            }),
          ]}>
          <Grid
            container
            sx={{
              borderRadius: 0,
              minHeight: '600px',
              height: '100%', // Take full height of container
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between', // Distribute space between rows
            }}>
            {/* Top Section */}
            <Grid container>
              <Grid size={{ xs: 7, md: 7 }} />
              <Grid
                size={{ xs: 5, md: 5 }}
                marginTop={10}
                alignItems="top"
                sx={{ display: 'flex', justifyContent: 'right', minHeight: '300px' }}>
                <m.div style={{ y: y1 }}>{renderHeading()}</m.div>
              </Grid>
            </Grid>

            {/* Bottom Section - will align to bottom */}
            <Grid container alignItems="center" sx={{ marginBottom: 4 }}>
              <Grid size={{ xs: 12, md: 8 }} sx={{ justifyContent: 'right' }}>
                <m.div style={{ y: y1 }}>{renderCard()}</m.div>
              </Grid>

              <Grid size={{ xs: 0, md: 4 }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'right' }}>
                <Box 
                  component={m.div} 
                  variants={varFade('in')} 
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {renderWheel()}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

function useTransformY(value: MotionValue<number>, distance: number) {
  const physics: SpringOptions = {
    mass: 0.1,
    damping: 20,
    stiffness: 300,
    restDelta: 0.001,
  };

  return useSpring(useTransform(value, [0, 1], [0, distance]), physics);
}

function useScrollPercent() {
  const elementRef = useRef<HTMLDivElement>(null);

  const { scrollY } = useScroll();

  const [percent, setPercent] = useState(0);

  useMotionValueEvent(scrollY, 'change', (scrollHeight) => {
    let heroHeight = 0;

    if (elementRef.current) {
      heroHeight = elementRef.current.offsetHeight;
    }

    const scrollPercent = Math.floor((scrollHeight / heroHeight) * 100);

    if (scrollPercent >= 100) {
      setPercent(100);
    } else {
      setPercent(Math.floor(scrollPercent));
    }
  });

  return { elementRef, percent, scrollY };
}
